// src/delivery_efficiency.js — generated from the same source as backend/delivery_efficiency.js; keep them identical.
// Delivery efficiency (2026-10). Pure; the same code runs in the backend
// (branch comparison) and the browser (the Efficiency tab).
//
// Input: visits per patient — one entry per patient per delivery round:
//   { d: 'YYYY-MM-DD' (UK day), t: 'HH:MM', r: roundId, v: driverId, i: items, s: true|false }
//   s = true when anything on that round was delivered, false when the round's
//   attempt(s) all failed.
//
// Definitions:
//   • A delivered visit is AVOIDABLE when it falls within `windowDays` of the
//     first delivered visit of its group — the group needed one trip, not two.
//   • A delivered visit that follows a failed attempt (with nothing delivered
//     in between) is a REDELIVERY: necessary, counted separately.
//   • SAME-DAY repeat: an avoidable visit on the same day as the previous one.
//   • Gap: days between consecutive delivered visits.
//   • Cycle: the median of a patient's gaps longer than the window; a gap is
//     ON CYCLE within ±max(3 days, 20%) of it.
//   • Regularity: spread of those cycle gaps (coefficient of variation).
//   • Efficiency = necessary delivered visits ÷ delivered visits.

const dayNo = (ymd) => Math.round(Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 86400000);
const median = (xs) => { if (!xs.length) return null; const s = [...xs].sort((a, b) => a - b); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

function cycleLabel(c) {
  if (c == null) return '';
  if (c >= 6 && c <= 8) return 'weekly';
  if (c >= 13 && c <= 15) return 'fortnightly';
  if (c >= 20 && c <= 22) return '3-weekly';
  if (c >= 26 && c <= 32) return c <= 29 ? '28-day' : 'monthly';
  if (c >= 54 && c <= 62) return '8-weekly';
  return `every ${Math.round(c)} days`;
}

function analysePatient(list, W) {
  const visits = [...list].sort((a, b) => (a.d + (a.t || '')).localeCompare(b.d + (b.t || '')))
    .map((v) => ({ ...v, n: dayNo(v.d), flag: '', gap: null, cause: '' }));
  let anchor = null, lastDelivered = null, failedSince = null;
  for (const v of visits) {
    if (!v.s) { v.flag = 'failed'; failedSince = failedSince ?? v; continue; }
    if (lastDelivered) v.gap = v.n - lastDelivered.n;
    if (failedSince && v.n - failedSince.n <= W) {
      v.flag = 'redelivery';
      v.cause = `Redelivery ${v.n - failedSince.n === 0 ? 'the same day' : `${v.n - failedSince.n} day${v.n - failedSince.n === 1 ? '' : 's'}`} after a failed drop`;
    } else if (anchor && v.n - anchor.n <= W) {
      const same = v.n === lastDelivered.n;
      v.flag = same ? 'sameday' : 'avoidable';
      v.cause = same
        ? 'Same day, another round — items probably weren’t ready for the first round'
        : `${v.gap} day${v.gap === 1 ? '' : 's'} after the last delivery — could have gone together (scripts or owings ready on different days?)`;
    } else {
      anchor = v; v.flag = 'ok';
    }
    lastDelivered = v; failedSince = null;
  }
  const delivered = visits.filter((v) => v.s);
  const gaps = delivered.filter((v) => v.gap != null).map((v) => v.gap);
  const cycleGaps = gaps.filter((g) => g > W);
  const cycle = median(cycleGaps);
  let cv = null;
  if (cycleGaps.length >= 2) {
    const mean = cycleGaps.reduce((t, g) => t + g, 0) / cycleGaps.length;
    const sd = Math.sqrt(cycleGaps.reduce((t, g) => t + (g - mean) ** 2, 0) / cycleGaps.length);
    cv = mean ? sd / mean : 0;
  }
  const avoidable = delivered.filter((v) => v.flag === 'avoidable' || v.flag === 'sameday').length;
  const sameDay = delivered.filter((v) => v.flag === 'sameday').length;
  const redeliveries = delivered.filter((v) => v.flag === 'redelivery').length;
  const failed = visits.length - delivered.length;
  // Gap classes, for the histogram.
  for (const v of delivered) {
    if (v.gap == null) continue;
    v.gapClass = v.flag === 'redelivery' ? 'redelivery'
      : v.gap <= W ? 'bunched'
      : cycle != null && Math.abs(v.gap - cycle) <= Math.max(3, cycle * 0.2) ? 'cycle' : 'off';
  }
  let pattern;
  if (delivered.length < 3) pattern = avoidable ? 'bunched' : 'few';
  else if (avoidable >= 2 || avoidable / delivered.length >= 0.2) pattern = 'bunched';
  else if (cv != null && cv > 0.35) pattern = 'irregular';
  else pattern = 'steady';
  return {
    visits, delivered: delivered.length, necessary: delivered.length - avoidable, avoidable, sameDay, redeliveries, failed,
    medianGap: median(gaps), cycle, cycleLabel: cycleLabel(cycle), cv, pattern,
    items: delivered.reduce((t, v) => t + (v.i || 0), 0),
  };
}

const HIST_MAX = 42; // gaps of 42+ days share the last bar

function analyseEfficiency(visitsByPatient, { windowDays = 5 } = {}) {
  const W = Math.max(0, Math.min(30, Math.round(Number(windowDays) || 0)));
  const patients = [];
  const hist = Array.from({ length: HIST_MAX + 1 }, (_, g) => ({ gap: g, bunched: 0, redelivery: 0, cycle: 0, off: 0 }));
  const monthly = new Map();
  const k = { delivered: 0, necessary: 0, avoidable: 0, sameDay: 0, redeliveries: 0, failed: 0, patients: 0, steady: 0, judged: 0, items: 0 };
  for (const [patientId, list] of Object.entries(visitsByPatient || {})) {
    if (!list || !list.length) continue;
    const a = analysePatient(list, W);
    patients.push({ patientId, ...a });
    k.patients++; k.delivered += a.delivered; k.necessary += a.necessary; k.avoidable += a.avoidable;
    k.sameDay += a.sameDay; k.redeliveries += a.redeliveries; k.failed += a.failed; k.items += a.items;
    if (a.delivered >= 3) { k.judged++; if (a.pattern === 'steady') k.steady++; }
    for (const v of a.visits) {
      const m = v.d.slice(0, 7);
      const row = monthly.get(m) || { month: m, delivered: 0, avoidable: 0, sameDay: 0, redeliveries: 0, failed: 0 };
      if (!v.s) row.failed++;
      else {
        row.delivered++;
        if (v.flag === 'avoidable' || v.flag === 'sameday') row.avoidable++;
        if (v.flag === 'sameday') row.sameDay++;
        if (v.flag === 'redelivery') row.redeliveries++;
      }
      monthly.set(m, row);
      if (v.gapClass) hist[Math.min(v.gap, HIST_MAX)][v.gapClass]++;
    }
  }
  patients.sort((x, y) => y.avoidable - x.avoidable || y.sameDay - x.sameDay || (y.cv || 0) - (x.cv || 0) || y.delivered - x.delivered);
  const kpi = { ...k, windowDays: W,
    efficiency: k.delivered ? k.necessary / k.delivered : null,
    avoidablePct: k.delivered ? k.avoidable / k.delivered : null,
    steadyPct: k.judged ? k.steady / k.judged : null };
  return { kpi, hist, monthly: [...monthly.values()].sort((a, b) => a.month.localeCompare(b.month)), patients };
}

export { analyseEfficiency, analysePatient, cycleLabel, dayNo, HIST_MAX };
